import type { Address } from "viem";
import type { RedKeel } from "../config/networks";

/** Exploradores de bloques, uno por red. */
export const EXPLORADOR: Record<RedKeel["clave"], string> = {
  arbitrum: "https://arbiscan.io",
  robinhood: "https://robinhoodchain.blockscout.com",
  hyperevm: "https://hyperevmscan.io",
  bnb: "https://bscscan.com",
  // BaseScan (Etherscan): los 5 contratos de Base están verificados aquí (además de Sourcify y
  // Blockscout). Es el explorador que reconocen las wallets (Rabby/MetaMask), así que el código se
  // lee tanto desde el app como al firmar.
  base: "https://basescan.org",
  ethereum: "https://etherscan.io",
};

/**
 * Donde MIRAR la pool: el sitio que enseña precio, volumen, TVL y profundidad — lo que el dueño
 * quiere ver cuando se pregunta si su par se mueve.
 *
 * `dex` y `sitio` NO son lo mismo y por eso son dos campos. En Arbitrum y Robinhood la pool es de
 * Uniswap y la pagina tambien; en HyperEVM la pool es de PRJX, que es un fork de Uniswap V3 y por
 * tanto NO aparece en app.uniswap.org. Mandarla alli daria una pagina vacia — la version clasica
 * hace justo eso y encima con la cadena equivocada (`.../pools/robinhood/<pool de HyperEVM>`).
 * PRJX no publica una pagina POR-POOL (los enlaces con la direccion dan 404): solo tiene /swap y
 * /liquidity. Por decision del dueño los botones de HyperEVM aterrizan en prjx.com/liquidity (el
 * DEX nativo) en vez de DexScreener; DexScreener seguia siendo la unica vista real de la pool
 * concreta (chainId hyperevm, dexId prjx), por si algun dia se quiere volver a ofrecer.
 */
type Enlaces = {
  /** Quien es dueño del mercado: lo que se dice al nombrar la pool. */
  dex: string;
  /** A donde lleva el enlace: lo que se dice en el boton, para que no prometa otra cosa. */
  sitio: string;
  pool: (pool: Address) => string;
  /** La posicion concreta (NFT). `null` donde no hay una pagina que la sepa enseñar. */
  posicion: ((tokenId: bigint) => string) | null;
};

const DEX: Record<RedKeel["clave"], Enlaces> = {
  // Ethereum es la red de ORIGEN de Uniswap: su explorador de pools no lleva prefijo de cadena
  // ("/explore/pools/ethereum/…" existe, pero el canónico es sin nada), y las posiciones V3 sí.
  ethereum: {
    dex: "Uniswap",
    sitio: "Uniswap",
    pool: (p) => `https://app.uniswap.org/explore/pools/ethereum/${p}`,
    posicion: (id) => `https://app.uniswap.org/positions/v3/ethereum/${id}`,
  },
  arbitrum: {
    dex: "Uniswap",
    sitio: "Uniswap",
    pool: (p) => `https://app.uniswap.org/explore/pools/arbitrum/${p}`,
    posicion: (id) => `https://app.uniswap.org/positions/v3/arbitrum/${id}`,
  },
  robinhood: {
    dex: "Uniswap",
    sitio: "Uniswap",
    pool: (p) => `https://app.uniswap.org/explore/pools/robinhood/${p}`,
    posicion: (id) => `https://app.uniswap.org/positions/v3/robinhood/${id}`,
  },
  hyperevm: {
    dex: "PRJX",
    sitio: "PRJX",
    // PRJX (fork de Uniswap V3) NO publica pagina por-pool ni por-posicion: cualquier URL con la
    // direccion de la pool da 404 (comprobado 2026-08-22 contra prjx.com). Solo existen /swap y
    // /liquidity (panel general de pools y posiciones). Antes esto iba a DexScreener —la unica vista
    // real de la pool concreta— pero el dueño pidio aterrizar EN PRJX, asi que los dos botones van a
    // /liquidity. Se ignora la direccion porque PRJX no ofrece una ruta que la reciba.
    pool: () => `https://www.prjx.com/liquidity`,
    posicion: () => `https://www.prjx.com/liquidity`,
  },
  // BNB Chain es PancakeSwap, no Uniswap: sus pools NO estan en app.uniswap.org y mandar alli daria
  // una pagina vacia, que es el fallo que ya tuvo la version clasica con HyperEVM. Pancake si tiene
  // pantalla propia para la pool Y para la posicion, asi que las dos van a su sitio.
  bnb: {
    dex: "PancakeSwap",
    sitio: "PancakeSwap",
    pool: (p) => `https://pancakeswap.finance/info/v3/pairs/${p}?chain=bsc`,
    posicion: (id) => `https://pancakeswap.finance/liquidity/${id}?chain=bsc`,
  },
  // Base es AERODROME SLIPSTREAM. Aerodrome NO tiene una pagina para MIRAR la pool: su unica URL
  // por-pool es `/deposit?token0..token1..type..factory..`, que es el formulario de AÑADIR liquidez
  // ("New deposit"), no lo que pide el boton "Ver la pool" (precio, volumen, TVL, profundidad).
  // Probado 2026-09-05: el deep-link acertaba la pool pero aterrizaba en el deposito y el dueño dijo
  // DECISION DEL DUEÑO 2026-09-05: LOS DOS botones de Base van EN AERODROME, sin excepcion. No a
  // DexScreener (era la "vista de mercado" pero el dueño quiere Aerodrome) ni al explorador. Aerodrome
  // solo tiene UNA URL por-pool: `/deposit?token0..token1..type=<tickSpacing>..factory..` (la pagina
  // de manage, que ADEMAS enseña precio actual, rango, APR y profundidad). Como pide token0/token1/
  // tickSpacing y no la direccion, el enlace se arma en `urlPool`/`urlPosicion` con el contexto del
  // vault (ver `urlPoolAerodrome`). Estos campos son solo el fallback si faltara ese contexto.
  base: {
    dex: "Aerodrome",
    sitio: "Aerodrome",
    pool: () => `https://aerodrome.finance/liquidity`,
    posicion: () => `https://aerodrome.finance/liquidity`,
  },
};

/** VER la pool EN Aerodrome (decision del dueño: todo en Aerodrome). La ÚNICA página por-pool de
 *  Aerodrome es `/deposit?token0..token1..type..factory..`, que muestra los datos pero con marco de
 *  "New deposit" (formulario de añadir liquidez) — el dueño lo rechazó ("quiero VER la pool, no un
 *  formulario"). Su otra vista es la LISTA de Liquidity, que sí es de solo-lectura (TVL, volumen, APR,
 *  fees) y su buscador acepta la dirección → se filtra a la pool concreta con `?query=<pool>`. El NFT
 *  de la posición lo posee el contrato del vault, así que Aerodrome no lo lista bajo tu wallet ni tiene
 *  ruta por-tokenId; por eso la posición también aterriza en la VISTA de su pool en Aerodrome. */
const urlPoolAerodrome = (pool: Address) =>
  `https://aerodrome.finance/liquidity?query=${pool.toLowerCase()}`;

export const nombreDex = (red: RedKeel) => DEX[red.clave].dex;
export const sitioPool = (red: RedKeel) => DEX[red.clave].sitio;

/** A donde lleva «Ver la pool»: la vista de mercado de esa pool. Base va a la lista de Liquidity de
 *  Aerodrome filtrada a la pool; el resto, a la página propia de su DEX. */
export const urlPool = (red: RedKeel, pool: Address) =>
  red.clave === "base" ? urlPoolAerodrome(pool) : DEX[red.clave].pool(pool);

/**
 * El NFT de la posición en el EXPLORADOR. Cada familia de exploradores lo pone en otra ruta y no
 * hay una que valga para las dos: Blockscout usa `/token/<contrato>/instance/<id>` y los de estilo
 * Etherscan (arbiscan, hyperevmscan) usan `/nft/<contrato>/<id>`. Con la ruta cruzada sale un 404,
 * que en un enlace de auditoría es peor que no ponerlo — parece que la posición no existe.
 */
export const urlNft = (red: RedKeel, npm: Address, tokenId: bigint) =>
  red.clave === "robinhood"
    ? `${EXPLORADOR[red.clave]}/token/${npm}/instance/${tokenId}` // Blockscout
    : `${EXPLORADOR[red.clave]}/nft/${npm}/${tokenId}`; // estilo Etherscan (incl. BaseScan)

/**
 * Dónde mirar TU posición, no la pool entera. Siempre devuelve algo: donde el DEX tiene pantalla
 * propia (Uniswap) va ahí, porque enseña el rango y lo acumulado; donde no la tiene (PRJX no
 * publica una) cae al NFT en el explorador, que al menos demuestra que la posición existe y es del
 * vault. Devolver `null` obligaría a esconder el botón en una de las tres redes, y un botón que
 * está o no está según dónde estés conectado se lee como que algo falla.
 */
export function urlPosicion(red: RedKeel, npm: Address, tokenId: bigint, vault?: Address) {
  // Base = Aerodrome: la posicion se enseña en AeroBoard (analitica de Aerodrome). INVESTIGADO Y
  // VERIFICADO 2026-09-05 (api.positions?address=vault): aerodrome.finance NO puede mostrar esta
  // posicion porque su app es solo-wallet-conectada y el NFT lo posee el CONTRATO del vault (no la
  // wallet del dueño), y Aerodrome —a diferencia de Uniswap— NO tiene visor publico por-tokenId.
  // AeroBoard lee las posiciones de CUALQUIER direccion on-chain (incluida nuestra factory 0xf8f2) y
  // enseña par, rango (tick_lower/upper), dentro/fuera, valor y comisiones — como el visor de Uniswap.
  if (red.clave === "base" && vault) return { url: `https://aeroboard.app/address/${vault.toLowerCase()}`, sitio: "AeroBoard" };
  const propia = DEX[red.clave].posicion?.(tokenId);
  return propia
    ? { url: propia, sitio: DEX[red.clave].sitio }
    : { url: urlNft(red, npm, tokenId), sitio: "el explorador" };
}

/**
 * Arkham Intelligence: el explorador de "seguir el dinero". Una sola URL por dirección enseña su
 * actividad en TODAS las cadenas a la vez (cubre las seis de Keel: Ethereum, Arbitrum, Base, BNB,
 * HyperEVM y Robinhood Chain) con etiquetas de entidades, flujos y contrapartes. Es la auditoría que
 * cualquiera puede hacer sin saber leer un explorador de bloques.
 */
export const urlArkham = (direccion: string) => `https://arkm.com/explorer/address/${direccion}`;
